import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, Radio, Power, Shuffle, Hand } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import Reveal from "../components/Reveal";

const FREQ_MIN = 88;
const FREQ_MAX = 108;

const MOODS = ["Chilling", "Cooling", "Grooving", "Film making", "Walking", "Intensifying", "Fast energising", "Chaos Engineering"];
const moodPct = (i) => 5 + (i / (MOODS.length - 1)) * 90;

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

// iOS Safari ignore la propriété volume des éléments audio — on le détecte une fois pour toutes
const VOLUME_CONTROL = (() => {
  if (typeof document === "undefined") return true;
  const a = document.createElement("audio");
  try {
    a.volume = 0.42;
    return a.volume === 0.42;
  } catch {
    return false;
  }
})();

function VUMeter({ playing, variant }) {
  return (
    <div className="jm-vu-face relative w-20 h-12 sm:w-24 sm:h-14 rounded-sm border border-jm-brass/25 overflow-hidden shrink-0" data-testid={`jukebox-vu-${variant}`}>
      {/* graduations */}
      <div className="absolute inset-x-1.5 top-1 flex justify-between font-mono text-[6px] tracking-widest text-jm-brass/80">
        {["-20", "-5", "0", "+3"].map((v) => (
          <span key={v} className={v === "+3" ? "text-red-400/90" : ""}>{v}</span>
        ))}
      </div>
      <div className="absolute right-1 top-3.5 bottom-1.5 w-0.5 rounded bg-red-500/25" />
      {/* aiguille */}
      <div
        className={`jm-vu-needle absolute bottom-0.5 left-1/2 h-[78%] w-[2px] bg-amber-200 rounded-full ${playing ? (variant === "l" ? "is-on-a" : "is-on-b") : ""}`}
        style={{ animationPlayState: playing ? "running" : "paused", boxShadow: "0 0 4px rgba(253,230,180,0.8)" }}
      />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-jm-ink border border-jm-brass/40" />
    </div>
  );
}

// Titre défilant de gauche à droite (avec pauses aux extrémités)
function Marquee({ text, className = "", style, testId }) {
  return (
    <p className={`overflow-hidden whitespace-nowrap ${className}`} style={style} data-testid={testId}>
      <span className="jm-marquee-track">
        <span className="inline-block px-10">{text}</span>
        <span className="inline-block px-10">{text}</span>
      </span>
    </p>
  );
}

function TapeReel({ spinning, testId, duration = 6, size = 400 }) {
  const cssSize = typeof size === "number" ? `${size}px` : size;
  // échelle calculée en JS : la division de longueurs dans calc() CSS est invalide sur Safari < 16.4 (iPad)
  const scale = typeof size === "number" ? size / 400 : 1;
  return (
    <div data-testid={testId} className="relative shrink-0" style={{ width: cssSize, height: cssSize }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <div
          className={`w-[400px] h-[400px] rounded-full relative ${spinning ? "jm-reel-spin" : ""}`}
          style={{
            animationPlayState: spinning ? "running" : "paused",
            animationDuration: `${duration}s`,
            background: "radial-gradient(circle at 35% 30%, #4d5158, #26282d 60%, #17181c)",
            boxShadow: "inset 0 2px 4px rgba(255,255,255,0.18), inset 0 -4px 8px rgba(0,0,0,0.6), 0 6px 18px rgba(0,0,0,0.5)",
          }}
        >
      {/* 6 fenêtres laissant voir la bande enroulée */}
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <div key={deg} className="absolute inset-0" style={{ transform: `rotate(${deg}deg)` }}>
          <div
            className="absolute left-1/2 top-[6%] -translate-x-1/2 w-[24%] h-[36%] rounded-[45%] border border-white/10"
            style={{ background: "repeating-radial-gradient(circle at 50% 130%, #241a10 0 2px, #160f08 2px 4px)", boxShadow: "inset 0 2px 6px rgba(0,0,0,0.8)" }}
          />
        </div>
      ))}
      {/* moyeu */}
      <div
        className="absolute inset-[35%] rounded-full"
        style={{ background: "radial-gradient(circle at 40% 35%, #6b6f76, #33353a 70%)", boxShadow: "inset 0 1px 2px rgba(255,255,255,0.25), 0 1px 3px rgba(0,0,0,0.6)" }}
      />
      {[0, 120, 240].map((deg) => (
        <div key={deg} className="absolute inset-0" style={{ transform: `rotate(${deg}deg)` }}>
          <div className="absolute left-1/2 top-[37%] -translate-x-1/2 w-1.5 h-[9%] bg-black/60 rounded-full" />
        </div>
      ))}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-jm-brass" />
        </div>
      </div>
    </div>
  );
}

function Knob({ value, onChange }) {
  const startRef = useRef(null);
  const angle = -135 + value * 270;
  return (
    <div className="flex flex-col items-center gap-1.5 select-none">
      <div
        className="jm-knob w-16 h-16 rounded-full relative cursor-ns-resize touch-none"
        data-testid="jukebox-knob"
        title="Volume — faites tourner"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          startRef.current = { y: e.clientY, v: value };
        }}
        onPointerMove={(e) => {
          if (!startRef.current) return;
          onChange(Math.min(1, Math.max(0, startRef.current.v + (startRef.current.y - e.clientY) / 140)));
        }}
        onPointerUp={() => (startRef.current = null)}
        onWheel={(e) => onChange(Math.min(1, Math.max(0, value - Math.sign(e.deltaY) * 0.06)))}
      >
        <div className="absolute inset-0" style={{ transform: `rotate(${angle}deg)` }}>
          <div className="absolute left-1/2 top-1.5 w-1 h-4.5 -translate-x-1/2 rounded-full bg-jm-amber" style={{ height: "1.1rem", boxShadow: "0 0 7px rgba(245,158,11,0.9)" }} />
        </div>
        <div className="absolute inset-[30%] rounded-full border border-black/50" style={{ background: "linear-gradient(180deg,#2c2e34,#16171c)" }} />
      </div>
      <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-jm-mute">Volume</span>
    </div>
  );
}

export default function Jukebox() {
  const [phase, setPhase] = useState("intro");
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [moodIdx, setMoodIdx] = useState(null);
  const [mode, setMode] = useState("manual"); // "manual" | "random"
  const [playing, setPlaying] = useState(false);
  const [tuning, setTuning] = useState(false);
  const [dragPct, setDragPct] = useState(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [playbackError, setPlaybackError] = useState(false);
  const [flash, setFlash] = useState(null);
  const audioRef = useRef(null);
  const audioBRef = useRef(null);
  const tunerRef = useRef(null);
  const flashTimer = useRef(null);
  const modeRef = useRef(mode);
  const moodRef = useRef(moodIdx);
  const tracksRef = useRef(tracks);
  const activeRef = useRef("a");
  const fadingRef = useRef(false);
  const fadeIntervalRef = useRef(null);
  const playingRef = useRef(playing);
  const volumeRef = useRef(volume);
  const audioCtxRef = useRef(null);
  modeRef.current = mode;
  moodRef.current = moodIdx;
  tracksRef.current = tracks;
  playingRef.current = playing;
  volumeRef.current = volume;

  useEffect(() => {
    const t = setTimeout(() => setPhase("on"), 3400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  const currentTrack = tracks.find((t) => t.id === currentId) || null;
  const artistName = (id) => artists.find((a) => a.id === id)?.name || "JOY MESSENGER";

  const stations = useMemo(() => {
    const ids = [...new Set(tracks.map((t) => t.artist || "joy-messenger"))];
    return ids.map((id, i) => ({
      id,
      name: artistName(id),
      freq: ids.length === 1 ? 98 : 92 + (i * (104 - 92)) / (ids.length - 1),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, artists]);

  const needlePct = dragPct ?? (moodIdx !== null ? moodPct(moodIdx) : 50);
  // vitesse de rotation des bobines selon l'ambiance : Chilling (très lent) -> Chaos Engineering (très rapide)
  const reelDuration = moodIdx !== null ? 22 - moodIdx * ((22 - 0.95) / (MOODS.length - 1)) : 6;
  const tapeDuration = moodIdx !== null ? 2.2 - moodIdx * ((2.2 - 0.2) / (MOODS.length - 1)) : 0.7;
  const needleMoodIdx = Math.round(Math.min(100, Math.max(0, needlePct - 5)) / 90 * (MOODS.length - 1));
  const freeTuning = dragPct !== null;

  const pickRandom = (arr, excludeId) => {
    const pool = arr.length > 1 && excludeId ? arr.filter((t) => t.id !== excludeId) : arr;
    return pool[Math.floor(Math.random() * pool.length)] || null;
  };

  const playMood = (i) => {
    const candidates = tracksRef.current.filter((t) => t.mood === MOODS[i]);
    const t = pickRandom(candidates, currentId);
    if (!t) {
      setFlash(`Aucun morceau « ${MOODS[i]} » pour l'instant`);
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 2600);
      setMoodIdx(i);
      setDragPct(moodPct(i));
      return;
    }
    applyTrack(t, i);
  };

  const playRandomMood = () => {
    const withTracks = MOODS.map((m, i) => i).filter((i) => tracksRef.current.some((t) => t.mood === MOODS[i]));
    if (!withTracks.length) return;
    let i = withTracks[Math.floor(Math.random() * withTracks.length)];
    if (withTracks.length > 1) {
      while (i === moodRef.current) i = withTracks[Math.floor(Math.random() * withTracks.length)];
    }
    playMood(i);
  };

  // Deux lecteurs se relaient : le morceau suivant démarre en fondu pendant que l'actuel s'estompe
  const activeAudio = () => (activeRef.current === "a" ? audioRef.current : audioBRef.current);
  const inactiveAudio = () => (activeRef.current === "a" ? audioBRef.current : audioRef.current);

  // iOS : chaque élément <audio> doit avoir été joué une fois dans un geste utilisateur,
  // sinon le play() du fondu enchaîné (déclenché en fin de morceau, sans geste) est bloqué
  const unlockedRef = useRef(new WeakSet());
  const unlockAudio = (el, srcUrl) => {
    if (!el || unlockedRef.current.has(el)) return;
    if (!el.src && srcUrl) el.src = srcUrl;
    if (!el.src) return;
    el.muted = true;
    // garde-fou : ne jamais rester muet si la promesse play() ne répond pas (protections navigateur)
    const unmute = setTimeout(() => { el.muted = false; }, 1200);
    const p = el.play();
    if (p && p.then) {
      p.then(() => {
        clearTimeout(unmute);
        unlockedRef.current.add(el);
        el.pause();
        el.currentTime = 0;
        el.muted = false;
      }).catch(() => {
        clearTimeout(unmute);
        el.muted = false;
      });
    } else {
      clearTimeout(unmute);
      el.muted = false;
    }
  };

  // Fondu enchaîné via Web Audio : iOS ignore la propriété volume des éléments <audio>,
  // seuls des nœuds de gain permettent un vrai fondu sur iPhone/iPad.
  // Les MP3 sont servis par notre propre API (même origine) : pas de souci CORS.
  const gainsRef = useRef({ a: null, b: null });
  const webAudioDeadRef = useRef(false); // pipeline silencieuse détectée → moteur classique définitif
  const pipelineProbeRef = useRef(null);
  const [audioKey, setAudioKey] = useState(0); // incrémenté pour monter des lecteurs neufs si le pipeline est muet

  // Certains navigateurs (protections de Brave) laissent le contexte « actif » mais coupent le son :
  // on mesure le signal réel ; s'il reste plat alors qu'un morceau joue, on abandonne Web Audio proprement.
  const schedulePipelineProbe = () => {
    const ctx = audioCtxRef.current;
    if (!ctx || pipelineProbeRef.current) return;
    let samples = 0;
    const probe = setInterval(() => {
      samples += 1;
      const el = activeAudio();
      const g = gainsRef.current[activeRef.current];
      if (!el || el.paused || !g || webAudioDeadRef.current) {
        if (samples > 6) { clearInterval(probe); pipelineProbeRef.current = null; }
        return;
      }
      try {
        const an = ctx.createAnalyser();
        an.fftSize = 512;
        g.connect(an);
        const data = new Uint8Array(an.fftSize);
        an.getByteTimeDomainData(data);
        g.disconnect(an);
        if (data.some((v) => Math.abs(v - 128) > 3)) {
          clearInterval(probe); // le son passe bien — fondu Web Audio confirmé
          pipelineProbeRef.current = null;
          return;
        }
      } catch { /* mesure impossible */ }
      if (samples >= 4) {
        clearInterval(probe);
        teardownWebAudio(); // signal plat pendant la lecture → pipeline muette
      }
    }, 400);
    pipelineProbeRef.current = probe;
  };

  const teardownWebAudio = () => {
    webAudioDeadRef.current = true;
    pipelineProbeRef.current = null;
    gainsRef.current = { a: null, b: null };
    // les éléments passés par createMediaElementSource restent muets une fois débranchés :
    // on en monte des neufs (via audioKey) et la piste en cours y est relancée
    [audioRef.current, audioBRef.current].forEach((el) => { try { el?.pause(); } catch { /* ok */ } });
    setAudioKey((k) => k + 1);
  };

  const ensureAudioGraph = () => {
    if (webAudioDeadRef.current) return; // pipeline muette constatée : moteur classique uniquement
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      try { audioCtxRef.current = new AC(); } catch { return; }
    }
    const ctx = audioCtxRef.current;
    if (ctx.state !== "running") {
      // certains navigateurs laissent le contexte suspendu : on retente resume()
      // et on NE route PAS encore les lecteurs dedans — sinon le son serait muet.
      ctx.resume().catch(() => {});
      return;
    }
    [["a", audioRef.current], ["b", audioBRef.current]].forEach(([side, el]) => {
      if (!el || gainsRef.current[side]) return;
      try {
        const node = ctx.createMediaElementSource(el);
        const g = ctx.createGain();
        g.gain.value = side === activeRef.current ? volumeRef.current : 0;
        node.connect(g);
        g.connect(ctx.destination);
        gainsRef.current[side] = g;
      } catch { /* élément déjà rattaché au graphe */ }
    });
    if (gainsRef.current.a && !pipelineProbeRef.current) schedulePipelineProbe();
  };

  // Niveau d'un lecteur : 0 → silence, 1 → volume du bouton. Via le gain Web Audio si dispo, sinon el.volume
  const setLevel = (side, factor) => {
    const g = gainsRef.current[side];
    const el = side === "a" ? audioRef.current : audioBRef.current;
    const v = Math.min(1, Math.max(0, factor * volumeRef.current));
    if (g) g.gain.value = v;
    else if (el) el.volume = v;
  };
  // Durée du fondu : 4 s si le niveau est contrôlable (Web Audio ou volume natif), sinon court
  const fadeSeconds = () => (VOLUME_CONTROL || gainsRef.current.a ? 4 : 1);

  const crossfadeTo = (t, i) => {
    const out = activeAudio();
    const inn = inactiveAudio();
    if (!out || !inn || !t) return;
    ensureAudioGraph();
    const outSide = activeRef.current;
    const inSide = outSide === "a" ? "b" : "a";
    fadingRef.current = true;
    const mi = i ?? MOODS.indexOf(t.mood);
    setCurrentId(t.id);
    setMoodIdx(mi);
    setDragPct(moodPct(mi));
    setPlaying(true);
    setPlaybackError(false);
    setTime(0);
    inn.src = mediaUrl(t.url);
    setLevel(inSide, 0);
    inn.play().then(() => {
      unlockedRef.current.add(inn);
    }).catch(() => {
      // iOS : l'élément entrant n'est pas déverrouillé — bascule directe sur l'élément sortant (lui, déjà déverrouillé)
      clearInterval(fadeIntervalRef.current);
      activeRef.current = activeRef.current === "a" ? "b" : "a"; // on reste sur l'élément sortant
      fadingRef.current = false;
      out.src = mediaUrl(t.url);
      setLevel(outSide, 1);
      setLevel(inSide, 0);
      out.play().then(() => unlockedRef.current.add(out)).catch(() => {
        setPlaying(false);
        setPlaybackError(true);
      });
      return;
    });
    activeRef.current = activeRef.current === "a" ? "b" : "a";
    const steps = fadeSeconds() * 20; // paliers de 50 ms
    let k = 0;
    clearInterval(fadeIntervalRef.current);
    fadeIntervalRef.current = setInterval(() => {
      k += 1;
      const p = Math.min(1, k / steps);
      setLevel(outSide, Math.max(0, 1 - p));
      setLevel(inSide, p);
      if (p >= 1) {
        clearInterval(fadeIntervalRef.current);
        out.pause();
        out.currentTime = 0;
        setLevel(outSide, 1);
        fadingRef.current = false;
      }
    }, 50);
  };

  const applyTrack = (t, i) => {
    const mi = i ?? MOODS.indexOf(t.mood);
    const cur = activeAudio();
    ensureAudioGraph();
    if (playingRef.current && cur && cur.src && !cur.paused) {
      crossfadeTo(t, mi);
      return;
    }
    setCurrentId(t.id);
    setMoodIdx(mi);
    setDragPct(moodPct(mi));
    setPlaying(true);
    if (cur && t) {
      const url = mediaUrl(t.url);
      if (cur.src !== url) cur.src = url;
      setLevel(activeRef.current, 1);
      setLevel(activeRef.current === "a" ? "b" : "a", 0);
      cur.play().catch((err) => {
        console.warn("jukebox play:", err.name);
        setPlaying(false);
        setPlaybackError(true);
      });
      unlockAudio(inactiveAudio(), url);
    }
  };

  // Enchaînement de fin de morceau : manuel = autre titre du même mood, aléatoire = mood aléatoire
  const handleEnded = () => {
    if (modeRef.current === "random") playRandomMood();
    else if (moodRef.current !== null) playMood(moodRef.current);
  };

  // Lecture audio — deux éléments <audio> montés dans le DOM, celui actif pilote l'affichage
  useEffect(() => {
    const els = [audioRef.current, audioBRef.current].filter(Boolean);
    if (!els.length) return;
    const onTime = (e) => {
      if (e.target !== activeAudio()) return;
      setTime(e.target.currentTime);
      const d = e.target.duration;
      if (d && playingRef.current && !fadingRef.current && d - e.target.currentTime <= fadeSeconds() + 0.25) {
        if (modeRef.current === "random") playRandomMood();
        else if (moodRef.current !== null) playMood(moodRef.current);
      }
    };
    const onDur = (e) => {
      if (e.target === activeAudio()) setDuration(e.target.duration || 0);
    };
    const onEnd = (e) => {
      if (e.target !== activeAudio() || fadingRef.current) return;
      handleEnded();
    };
    const onErr = (e) => {
      if (e.target !== activeAudio()) return;
      setPlaying(false);
      setPlaybackError(true);
    };
    els.forEach((el) => {
      el.addEventListener("timeupdate", onTime);
      el.addEventListener("loadedmetadata", onDur);
      el.addEventListener("ended", onEnd);
      el.addEventListener("error", onErr);
    });
    return () => {
      clearInterval(pipelineProbeRef.current);
      pipelineProbeRef.current = null;
      els.forEach((el) => {
        el.removeEventListener("timeupdate", onTime);
        el.removeEventListener("loadedmetadata", onDur);
        el.removeEventListener("ended", onEnd);
        el.removeEventListener("error", onErr);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioKey]);

  // Après abandon du pipeline Web Audio (navigateur qui la coupe), les lecteurs neufs reprennent la piste en cours
  useEffect(() => {
    if (audioKey === 0) return;
    const t = currentTrack;
    if (t && playingRef.current) {
      const a = audioRef.current;
      if (a) {
        a.src = mediaUrl(t.url);
        a.volume = volumeRef.current;
        a.play().then(() => unlockedRef.current.add(a)).catch(() => {
          setPlaying(false);
          setPlaybackError(true);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioKey]);

  useEffect(() => {
    const audio = activeAudio();
    if (!audio || !currentTrack) return;
    const url = mediaUrl(currentTrack.url);
    if (audio.src !== url) {
      audio.src = url;
      setTime(0);
      setPlaybackError(false);
      if (playing) audio.play().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.url]);

  useEffect(() => {
    if (!fadingRef.current) {
      setLevel("a", activeRef.current === "a" ? 1 : 0);
      setLevel("b", activeRef.current === "b" ? 1 : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume]);

  const togglePlay = () => {
    const audio = activeAudio();
    if (!audio) return;
    ensureAudioGraph();
    if (!currentTrack) {
      if (moodIdx !== null) playMood(moodIdx);
      return;
    }
    if (playing) {
      audio.pause();
      if (fadingRef.current) inactiveAudio()?.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch((err) => {
        console.warn("jukebox play:", err.name);
        setPlaying(false);
        setPlaybackError(true);
      });
      if (fadingRef.current) inactiveAudio()?.play().catch(() => {});
      unlockAudio(inactiveAudio(), audio.src);
    }
  };

  const next = () => {
    if (moodIdx !== null) playMood(moodIdx);
  };
  const prev = () => {
    const audio = activeAudio();
    if (audio) {
      audio.currentTime = 0;
      if (!playing) togglePlay();
    }
  };

  // Tuner manuel — l'aiguille pioche un morceau au hasard dans l'ambiance visée
  const tuneTo = (clientX, snap = false) => {
    if (!tunerRef.current) return null;
    const rect = tunerRef.current.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setDragPct(pct * 100);
    if (snap) {
      // l'aiguille pioche directement dans l'ambiance visée (pas d'aimantation aux stations)
      const i = Math.round(Math.min(100, Math.max(0, pct * 100 - 5)) / 90 * (MOODS.length - 1));
      if (i !== moodIdx || !currentId) playMood(i);
      else setDragPct(moodPct(i));
    }
    return null;
  };

  return (
    <main data-testid="jukebox-page" className="pt-28 sm:pt-36 min-h-screen relative overflow-x-clip">
      <audio
        key={`audio-a-${audioKey}`}
        ref={audioRef}
        controls
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute w-px h-px opacity-0 pointer-events-none overflow-hidden"
        style={{ clipPath: "inset(50%)" }}
        data-testid="jukebox-audio"
      />
      <audio
        key={`audio-b-${audioKey}`}
        ref={audioBRef}
        controls
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute w-px h-px opacity-0 pointer-events-none overflow-hidden"
        style={{ clipPath: "inset(50%)" }}
        data-testid="jukebox-audio-b"
      />
      {/* Mise en scène d'entrée : les plombs sautent, puis tout se rallume */}
      <AnimatePresence>
        {phase === "intro" && (
          <motion.div
            key="blackout"
            className="fixed inset-0 z-[70] bg-jm-ink flex flex-col items-center justify-center gap-6"
            data-testid="jukebox-intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0.35, 1, 0.9, 1, 1] }}
            exit={{ opacity: 0, transition: { duration: 0.6, ease: "easeOut" } }}
            transition={{ duration: 3.2, times: [0, 0.12, 0.24, 0.38, 0.5, 0.62, 1], ease: "linear" }}
          >
            <motion.p
              className="font-mono text-[11px] uppercase tracking-[0.35em] text-jm-mute"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0, 1, 1, 0] }}
              transition={{ duration: 2.6, times: [0, 0.35, 0.5, 0.85, 1] }}
            >
              … les plombs ont sauté …
            </motion.p>
            <motion.div
              className="w-3 h-3 rounded-full bg-jm-amber"
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: [0, 0, 1], scale: [0.4, 0.4, 1.4] }}
              transition={{ duration: 3, times: [0, 0.78, 1] }}
              style={{ boxShadow: "0 0 40px 12px rgba(245,158,11,0.45)" }}
            />
            <button
              onClick={() => setPhase("on")}
              data-testid="jukebox-skip-intro"
              className="absolute bottom-10 font-mono text-[10px] uppercase tracking-[0.25em] text-jm-mute hover:text-jm-bone transition-colors"
            >
              Passer l'intro
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="jm-container pb-24">
        <Reveal>
          <p className="jm-overline mb-5 flex items-center gap-2">
            <Radio size={14} /> Jukebox
          </p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-jm-bone leading-[1.08] max-w-4xl">
            Choisissez une ambiance, <em className="italic text-jm-brass">le jukebox fait le reste.</em>
          </h1>
          <p className="mt-5 text-jm-smoke max-w-2xl leading-relaxed">
            Posez l'aiguille sur une humeur — de Chilling à Chaos Engineering — et le jukebox y pioche un morceau au hasard,
            tiré de nos archives maison. Aucun streaming tiers.
          </p>
        </Reveal>

        {/* Le poste */}
        <Reveal delay={0.15}>
          <div className="jm-metal jm-brushed mt-14 rounded-xl border border-black/70 p-5 sm:p-10" data-testid="jukebox-deck">
            {/* Barre d'état — ordinateur uniquement (masquée sur téléphone et tablette pour remonter les bobines) */}
            <div className="hidden xl:flex flex-wrap items-center justify-center gap-6 mb-8 relative z-20">
              <div className="flex items-center gap-3">
                <span
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-500 ${playing ? "bg-jm-amber" : "bg-jm-mute/50"}`}
                  style={playing ? { boxShadow: "0 0 14px 3px rgba(245,158,11,0.6)" } : {}}
                  data-testid="jukebox-power-lamp"
                />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-jm-mute">JM-88 · Récepteur</span>
              </div>
              {/* Manuel / Aléatoire */}
              <button
                onClick={() => setMode(mode === "manual" ? "random" : "manual")}
                data-testid="jukebox-mode-toggle"
                className={`flex items-center gap-2 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                  mode === "random" ? "border-jm-amber text-jm-amber" : "border-jm-brass/30 text-jm-mute hover:text-jm-bone"
                }`}
                title={mode === "random" ? "Enchaînement : ambiance aléatoire à chaque morceau" : "Enchaînement : même ambiance, morceau aléatoire"}
              >
                {mode === "random" ? <Shuffle size={12} /> : <Hand size={12} />}
                {mode === "random" ? "Aléatoire" : "Manuel"}
              </button>
            </div>

            {/* Zone magnétophone : bobines géantes + bande en diagonale 88 → 108 */}
            <div className="relative flex flex-col" data-testid="jukebox-tapezone">
              {/* bobine émettrice / réceptrice — trois fois plus grosses, hautes, débordent du cadre */}
              <div className="hidden xl:block absolute -top-32 left-[-90px] z-40 pointer-events-none">
                <TapeReel spinning={playing} duration={reelDuration} testId="jukebox-reel-left" />
              </div>
              <div className="hidden xl:block absolute -top-32 right-[-90px] z-40 pointer-events-none">
                <TapeReel spinning={playing} duration={reelDuration} testId="jukebox-reel-right" />
              </div>
              {/* bande : sort par la gauche de la bobine émettrice, entre en diagonale sur 88,
                  traverse la ligne des fréquences, ressort de 108 en diagonale vers la droite de la réceptrice */}
              <svg
                className="hidden xl:block absolute inset-0 w-full h-full z-30 pointer-events-none"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ overflow: "visible" }}
                data-testid="jukebox-tape-band"
              >
                <line x1="0" y1="43" x2="5" y2="80" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
                <line x1="5" y1="80" x2="95" y2="80" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
                <line x1="95" y1="80" x2="100" y2="43" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
              </svg>

              {/* Bobines compactes en haut — téléphone (150px) */}
            <div className="sm:hidden order-1 relative z-40 pointer-events-none flex items-start justify-between px-0 -mx-4 mt-1" data-testid="jukebox-reels-compact">
              <TapeReel spinning={playing} duration={reelDuration} size={150} testId="jukebox-reel-left-sm" />
              <TapeReel spinning={playing} duration={reelDuration} size={150} testId="jukebox-reel-right-sm" />
            </div>

            {/* Bobines en haut, agrandies — tablette (260px) */}
            <div className="hidden sm:flex xl:hidden order-1 relative z-40 pointer-events-none items-start justify-between px-0 -mx-4 mt-1" data-testid="jukebox-reels-compact-md">
              <TapeReel spinning={playing} duration={reelDuration} size={260} testId="jukebox-reel-left-md" />
              <TapeReel spinning={playing} duration={reelDuration} size={260} testId="jukebox-reel-right-md" />
            </div>

            {/* bande — version téléphone : part de l'extrémité de la bobine émettrice, descend sur 88,
                passe sur la règle graduée (ligne des fréquences), ressort de 108 vers l'extrémité de la réceptrice.
                Hauteur fixe = bobines (150) + écart (32) + règle du tuner (~78) pour rester ancrée quelle que soit la hauteur de l'écran */}
            <svg
              className="sm:hidden absolute top-0 left-0 w-full h-[264px] z-30 pointer-events-none"
              viewBox="0 0 1000 264"
              preserveAspectRatio="none"
              style={{ overflow: "visible" }}
              data-testid="jukebox-tape-band-sm"
            >
              <line x1="-52" y1="79" x2="0" y2="262" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
              <line x1="0" y1="262" x2="1000" y2="262" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
              <line x1="1000" y1="262" x2="1052" y2="79" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
            </svg>

            {/* bande — version tablette : sort de la bobine de gauche, rentre dans la règle graduée (88),
                la traverse, ressort à 108 et rerentre dans la bobine de droite.
                Hauteur fixe = bobines (260) + écart (32) + règle du tuner (~78) */}
            <svg
              className="hidden sm:block xl:hidden absolute top-0 left-0 w-full h-[374px] z-30 pointer-events-none"
              viewBox="0 0 1000 374"
              preserveAspectRatio="none"
              style={{ overflow: "visible" }}
              data-testid="jukebox-tape-band-md"
            >
              <line x1="-22" y1="134" x2="0" y2="368" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
              <line x1="0" y1="368" x2="1000" y2="368" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
              <line x1="1000" y1="368" x2="1022" y2="134" stroke="#2e2010" strokeWidth="3" vectorEffect="non-scaling-stroke" className={playing ? "jm-tape-dash" : ""} style={{ animationDuration: `${tapeDuration}s` }} />
            </svg>

            {/* Contrôles — ordinateur */}
            <div className="mt-2 hidden xl:flex xl:order-1 flex-wrap items-center justify-center gap-4 sm:gap-6 relative z-20">
              <button onClick={prev} disabled={!currentTrack} data-testid="jukebox-prev" className="jm-btn-metal p-3 rounded-md text-jm-bone hover:text-jm-amber transition-colors disabled:opacity-40" title="Reprendre le morceau">
                <SkipBack size={16} />
              </button>
              <button
                onClick={togglePlay}
                disabled={!currentTrack && moodIdx === null}
                data-testid="jukebox-play"
                className="jm-btn-metal w-16 h-16 rounded-full flex items-center justify-center transition-all disabled:opacity-40"
              >
                <span className="w-12 h-12 rounded-full bg-jm-amber text-jm-ink flex items-center justify-center hover:bg-jm-gold transition-colors" style={{ boxShadow: "0 0 18px rgba(245,158,11,0.35), inset 0 1px 2px rgba(255,255,255,0.5)" }}>
                  {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                </span>
              </button>
              <button onClick={next} disabled={moodIdx === null} data-testid="jukebox-next" className="jm-btn-metal p-3 rounded-md text-jm-bone hover:text-jm-amber transition-colors disabled:opacity-40" title="Autre morceau de cette ambiance (au hasard)">
                <SkipForward size={16} />
              </button>
              {/* bouton volume rotatif */}
              <Knob value={volume} onChange={setVolume} />
            </div>

            {/* Fenêtre d'affichage rétroéclairée — VU-mètres resserrés autour du titre */}
              <div className="jm-backlit order-3 xl:order-2 relative z-20 mt-6 xl:mt-16 rounded border border-black/60 px-5 sm:px-8 h-28 sm:h-52 xl:h-32 flex flex-col xl:flex-row items-center justify-center gap-4 sm:gap-5 overflow-hidden xl:mx-[330px]" data-testid="jukebox-display">
              <div className="jm-grain absolute inset-0 opacity-15 pointer-events-none" />
              <div className="hidden sm:flex flex-1 w-full min-w-0 items-center justify-center gap-4 sm:gap-6">
              <div className="shrink-0"><VUMeter playing={playing} variant="l" /></div>
              <div className="flex-1 min-w-0 text-center">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] jm-backlit-text" data-testid="jukebox-display-title">
                  {tuning || (freeTuning && !currentTrack)
                    ? `${MOODS[needleMoodIdx].toUpperCase()}…`
                    : currentTrack
                      ? artistName(currentTrack.artist)
                      : `${MOODS[needleMoodIdx].toUpperCase()}…`}
                </p>
                {currentTrack ? (
                  <Marquee text={currentTrack.title} className="mt-2 font-serif text-xl sm:text-2xl text-amber-100" style={{ textShadow: "0 0 14px rgba(245,178,60,0.35)" }} testId="jukebox-now-playing" />
                ) : (
                  <p className="mt-2 font-serif text-xl sm:text-2xl text-amber-100 truncate" style={{ textShadow: "0 0 14px rgba(245,178,60,0.35)" }} data-testid="jukebox-now-playing">
                    — choisissez une ambiance sur le tuner —
                  </p>
                )}
                <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-jm-brass/80" data-testid="jukebox-display-sub">
                  {flash
                    ? flash
                    : playbackError
                      ? "Erreur de lecture — réessayez en cliquant une ambiance"
                      : currentTrack
                        ? `${fmt(time)} / ${fmt(duration)}${currentTrack.mood ? ` · ${currentTrack.mood}…` : ""}`
                        : tracks.length
                          ? "cliquez une ambiance ou posez-y l'aiguille"
                          : "ajoutez des morceaux depuis l'espace admin"}
                </p>
              </div>
              <div className="shrink-0"><VUMeter playing={playing} variant="r" /></div>
              </div>
              {/* Commandes de transport + VU-mètres en bas — version tablette */}
              <div className="hidden sm:flex xl:hidden items-center justify-center gap-5 relative z-20" data-testid="jukebox-controls-md">
                <button onClick={prev} disabled={!currentTrack} data-testid="jukebox-prev-md" className="jm-btn-metal p-2.5 rounded-md text-jm-bone hover:text-jm-amber transition-colors disabled:opacity-40" title="Reprendre le morceau">
                  <SkipBack size={15} />
                </button>
                <button
                  onClick={togglePlay}
                  disabled={!currentTrack && moodIdx === null}
                  data-testid="jukebox-play-md"
                  className="jm-btn-metal w-14 h-14 rounded-full flex items-center justify-center transition-all disabled:opacity-40"
                >
                  <span className="w-10 h-10 rounded-full bg-jm-amber text-jm-ink flex items-center justify-center hover:bg-jm-gold transition-colors" style={{ boxShadow: "0 0 18px rgba(245,158,11,0.35), inset 0 1px 2px rgba(255,255,255,0.5)" }}>
                    {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                  </span>
                </button>
                <button onClick={next} disabled={moodIdx === null} data-testid="jukebox-next-md" className="jm-btn-metal p-2.5 rounded-md text-jm-bone hover:text-jm-amber transition-colors disabled:opacity-40" title="Autre morceau de cette ambiance (au hasard)">
                  <SkipForward size={15} />
                </button>
                <button
                  onClick={() => setMode(mode === "manual" ? "random" : "manual")}
                  data-testid="jukebox-mode-toggle-md"
                  className={`flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                    mode === "random" ? "border-jm-amber text-jm-amber" : "border-jm-brass/30 text-jm-mute hover:text-jm-bone"
                  }`}
                >
                  {mode === "random" ? <Shuffle size={12} /> : <Hand size={12} />}
                  {mode === "random" ? "Aléatoire" : "Manuel"}
                </button>
                <Knob value={volume} onChange={setVolume} />
              </div>
              {/* Commandes de lecture — version téléphone, à la place des VU-mètres */}
              <div className="sm:hidden flex items-center justify-center gap-2 relative z-20" data-testid="jukebox-controls-sm">
                <button onClick={prev} disabled={!currentTrack} data-testid="jukebox-prev-sm" className="jm-btn-metal p-1.5 rounded-md text-jm-bone hover:text-jm-amber transition-colors disabled:opacity-40" title="Reprendre le morceau">
                  <SkipBack size={13} />
                </button>
                <button
                  onClick={togglePlay}
                  disabled={!currentTrack && moodIdx === null}
                  data-testid="jukebox-play-sm"
                  className="jm-btn-metal w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-40"
                >
                  <span className="w-8 h-8 rounded-full bg-jm-amber text-jm-ink flex items-center justify-center hover:bg-jm-gold transition-colors" style={{ boxShadow: "0 0 18px rgba(245,158,11,0.35), inset 0 1px 2px rgba(255,255,255,0.5)" }}>
                    {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                  </span>
                </button>
                <button onClick={next} disabled={moodIdx === null} data-testid="jukebox-next-sm" className="jm-btn-metal p-1.5 rounded-md text-jm-bone hover:text-jm-amber transition-colors disabled:opacity-40" title="Autre morceau de cette ambiance (au hasard)">
                  <SkipForward size={13} />
                </button>
                <button
                  onClick={() => setMode(mode === "manual" ? "random" : "manual")}
                  data-testid="jukebox-mode-toggle-sm"
                  className={`flex items-center gap-1 border px-2 py-1.5 font-mono text-[8px] uppercase tracking-[0.12em] transition-colors ${
                    mode === "random" ? "border-jm-amber text-jm-amber" : "border-jm-brass/30 text-jm-mute hover:text-jm-bone"
                  }`}
                  title={mode === "random" ? "Enchaînement : ambiance aléatoire à chaque morceau" : "Enchaînement : même ambiance, morceau aléatoire"}
                >
                  {mode === "random" ? <Shuffle size={10} /> : <Hand size={10} />}
                  {mode === "random" ? "Aléatoire" : "Manuel"}
                </button>
                <div className="origin-center scale-[0.7] -mx-3">
                  <Knob value={volume} onChange={setVolume} />
                </div>
              </div>
            </div>



            {/* commande de position — juste au-dessus du tuner (redescendue sur téléphone pour aérer) */}
            <div className="mt-14 sm:mt-10 order-4 xl:order-3 relative z-20">
              <div
                className="w-full h-6 flex items-center cursor-pointer group"
                data-testid="jukebox-progress"
                onClick={(e) => {
                  const audio = activeAudio();
                  if (!audio || !duration) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
                }}
              >
                <div className="relative w-full h-1.5 rounded-full bg-black/60 border border-jm-brass/15 group-hover:h-2 transition-all">
                  <div className="absolute left-0 top-0 h-full rounded-full bg-jm-amber" style={{ width: `${duration ? (time / duration) * 100 : 0}%`, boxShadow: "0 0 8px rgba(245,158,11,0.5)" }} />
                </div>
              </div>
            </div>

            {/* Titre + artiste + minutage — version téléphone, sous la barre de position */}
            <div className="sm:hidden order-5 mt-5 text-center relative z-20" data-testid="jukebox-track-info-sm">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] jm-backlit-text" data-testid="jukebox-display-artist-sm">
                {tuning || (freeTuning && !currentTrack)
                  ? `${MOODS[needleMoodIdx].toUpperCase()}…`
                  : currentTrack
                    ? artistName(currentTrack.artist)
                    : `${MOODS[needleMoodIdx].toUpperCase()}…`}
              </p>
              {currentTrack ? (
                <Marquee text={currentTrack.title} className="mt-1.5 font-serif text-xl text-amber-100" style={{ textShadow: "0 0 14px rgba(245,178,60,0.35)" }} testId="jukebox-now-playing-sm" />
              ) : (
                <p className="mt-1.5 font-serif text-xl text-amber-100 truncate" style={{ textShadow: "0 0 14px rgba(245,178,60,0.35)" }} data-testid="jukebox-now-playing-sm">
                  — choisissez une ambiance sur le tuner —
                </p>
              )}
              <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-jm-brass/80" data-testid="jukebox-display-time-sm">
                {flash
                  ? flash
                  : playbackError
                    ? "Erreur de lecture — réessayez en cliquant une ambiance"
                    : currentTrack
                      ? `${fmt(time)} / ${fmt(duration)}${currentTrack.mood ? ` · ${currentTrack.mood}…` : ""}`
                      : tracks.length
                        ? "cliquez une ambiance ou posez-y l'aiguille"
                        : "ajoutez des morceaux depuis l'espace admin"}
              </p>
            </div>

            {/* Tuner */}
            <div className="mt-8 select-none relative z-20 order-2 xl:order-4" data-testid="jukebox-tuner">
              <div
                ref={tunerRef}
                className="jm-tuner-glass relative h-40 cursor-pointer touch-none rounded border border-black/50"
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  setTuning(true);
                  tuneTo(e.clientX);
                }}
                onPointerMove={(e) => tuning && tuneTo(e.clientX)}
                onPointerUp={(e) => {
                  tuneTo(e.clientX, true);
                  setTuning(false);
                }}
              >
                {/* échelle des ambiances — cliquables, du plus cool au plus fast */}
                {MOODS.map((m, i) => {
                  const active = (freeTuning ? needleMoodIdx : moodIdx ?? needleMoodIdx) === i;
                  const has = tracks.some((t) => t.mood === m);
                  return (
                    <button
                      key={m}
                      data-testid={`jukebox-mood-${i}`}
                      onClick={() => playMood(i)}
                      className={`absolute -translate-x-1/2 font-serif italic text-xs sm:text-sm transition-colors duration-500 ${
                        active
                          ? "jm-backlit-text"
                          : has
                            ? "text-amber-100/75 hover:text-amber-100"
                            : "text-jm-mute/40 hover:text-jm-mute"
                      } ${i % 2 === 0 ? "top-2" : "top-[7.2rem]"}`}
                      style={{ left: `${moodPct(i)}%` }}
                    >
                      {m}…
                    </button>
                  );
                })}
                {/* ligne de la bande */}
                <div className="absolute left-0 right-0 top-[4.5rem] h-px bg-jm-brass/25" />
                {Array.from({ length: 41 }).map((_, i) => {
                  const f = FREQ_MIN + i * 0.5;
                  const major = f % 4 === 0;
                  return (
                    <div key={i} className="absolute top-[4.5rem]" style={{ left: `${(i / 40) * 100}%` }}>
                      <div className={`w-px ${major ? "h-3.5 bg-jm-brass/50" : "h-2 bg-jm-brass/25"}`} />
                      {major && (
                        <span className="absolute -left-3 top-4 font-mono text-[9px] text-jm-brass/80 tracking-widest">{f}</span>
                      )}
                    </div>
                  );
                })}
                {/* parasites pendant le réglage */}
                {tuning && <div className="jm-grain absolute inset-0 opacity-40 pointer-events-none" data-testid="jukebox-static" />}
                {/* marqueurs de stations */}
                {stations.map((s, i) => (
                  <button
                    key={s.id}
                    data-testid={`jukebox-station-${s.id}`}
                    onClick={() => {
                      const pool = tracks.filter((t) => (t.artist || "joy-messenger") === s.id);
                      const t = pickRandom(pool, currentId);
                      if (t) applyTrack(t, MOODS.indexOf(t.mood));
                    }}
                    className="absolute top-[2.2rem] -translate-x-1/2 group"
                    style={{ left: `${((s.freq - FREQ_MIN) / (FREQ_MAX - FREQ_MIN)) * 100}%` }}
                  >
                    <span className={`block font-mono text-[9px] uppercase tracking-[0.15em] whitespace-nowrap transition-colors ${currentTrack && (currentTrack.artist || "joy-messenger") === s.id ? "text-jm-amber" : "text-jm-mute"}`}>
                      {s.freq.toFixed(1)}
                    </span>
                  </button>
                ))}
                {/* aiguille */}
                <motion.div
                  className="absolute top-6 bottom-6 w-[3px] bg-jm-amber rounded-full pointer-events-none"
                  style={{ boxShadow: "0 0 16px 3px rgba(245,158,11,0.55)" }}
                  animate={{ left: `${needlePct}%` }}
                  transition={tuning ? { duration: 0 } : { type: "spring", stiffness: 120, damping: 16 }}
                  data-testid="jukebox-needle"
                />
              </div>
              <p className="mt-1 text-center font-mono text-[9px] uppercase tracking-[0.25em] text-jm-mute">
                Cliquez une ambiance ou posez-y l'aiguille — le jukebox pioche au hasard
              </p>
            </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <p className="mt-12 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-jm-mute">
            <Power size={12} /> Diffusé depuis les archives Joy Messenger — aucun streaming tiers.
          </p>
        </Reveal>
      </div>
    </main>
  );
}
